import React, { useEffect, useRef } from 'react';

/**
 * AppBuilderNetwork — full-width canvas "app-assembly" animation for the
 * AI App Builder hero section. 28 labeled nodes appear one-by-one on a
 * 320ms cadence, each spawning up to two travelling gold pulses to its
 * already-built neighbours. When all nodes are built the canvas pauses
 * ~3.2s, fades to empty, and rebuilds — looping indefinitely.
 *
 * Centered text overlay (pill badge + headline + subheadline) sits on
 * top of the canvas and is pointer-events: none so the canvas remains
 * visible behind it.
 */

const NODE_LABELS = [
  'Auth', 'Login', 'Sessions',                            // group 0
  'Dashboard', 'Analytics', 'Charts', 'Reports',          // group 1
  'API', 'Router', 'Cache', 'Webhooks',                   // group 2
  'Database', 'Users', 'Storage',                         // group 3
  'Payments', 'Invoices', 'Checkout',                     // group 4
  'UI', 'Forms', 'Search', 'Notifs',                      // group 5
  'Profile', 'Settings', 'Admin',                         // group 6
  'Deploy', 'CI/CD', 'Monitor', 'Logs',                   // group 7
];

const NODE_GROUPS = [
  0, 0, 0,
  1, 1, 1, 1,
  2, 2, 2, 2,
  3, 3, 3,
  4, 4, 4,
  5, 5, 5, 5,
  6, 6, 6,
  7, 7, 7, 7,
];

const GROUP_COLORS = [
  [180, 160, 100], // 0
  [140, 150, 130], // 1
  [160, 145, 110], // 2
  [130, 140, 125], // 3
  [170, 155, 105], // 4
  [135, 145, 135], // 5
  [155, 140, 115], // 6
  [125, 135, 120], // 7
];

const BUILD_INTERVAL_MS = 320;
const BUILD_FADE_MS = 600;
const PULSE_FRAMES = 80;
const RESET_DELAY_MS = 3200;
const CONNECTION_DISTANCE_PCT = 0.38;
const VELOCITY_RANGE = 0.00025;

const shuffledIndices = (n) => {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const AppBuilderNetwork = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    let W = 0;
    let H = 0;
    let dpr = 1;

    // World state
    const nodes = []; // { x, y (normalized 0..1), vx, vy, r, group, label, builtAt, opacity }
    const pulses = []; // { fromIdx, toIdx, t }
    let buildQueue = [];
    let lastBuildTime = performance.now();
    let resetAt = null;

    const initNodes = () => {
      nodes.length = 0;
      for (let i = 0; i < NODE_LABELS.length; i++) {
        nodes.push({
          x: 0.08 + Math.random() * 0.84,
          y: 0.08 + Math.random() * 0.84,
          vx: (Math.random() * 2 - 1) * VELOCITY_RANGE,
          vy: (Math.random() * 2 - 1) * VELOCITY_RANGE,
          r: 4 + Math.random() * 2.5,
          group: NODE_GROUPS[i],
          label: NODE_LABELS[i],
          builtAt: null,
          opacity: 0,
        });
      }
      pulses.length = 0;
      buildQueue = shuffledIndices(nodes.length);
      lastBuildTime = performance.now();
      resetAt = null;
    };

    const resize = () => {
      const rect = container.getBoundingClientRect();
      dpr = window.devicePixelRatio || 1;
      W = Math.max(1, rect.width);
      H = Math.max(1, rect.height);
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = `${W}px`;
      canvas.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      initNodes();
    };

    const distSq = (a, b) => {
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      return dx * dx + dy * dy;
    };

    const draw = (now) => {
      // ---------- Update positions (slow drift, bounce off 4–96% bounds) ----------
      for (const n of nodes) {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0.04 || n.x > 0.96) {
          n.vx = -n.vx;
          n.x = Math.max(0.04, Math.min(0.96, n.x));
        }
        if (n.y < 0.04 || n.y > 0.96) {
          n.vy = -n.vy;
          n.y = Math.max(0.04, Math.min(0.96, n.y));
        }
      }

      // ---------- Update opacities (fade in over BUILD_FADE_MS) ----------
      for (const n of nodes) {
        if (n.builtAt !== null) {
          const t = Math.min(1, (now - n.builtAt) / BUILD_FADE_MS);
          n.opacity = t;
        }
      }

      // ---------- Build cadence + reset ----------
      if (resetAt === null) {
        if (buildQueue.length > 0 && now - lastBuildTime >= BUILD_INTERVAL_MS) {
          const idx = buildQueue.shift();
          nodes[idx].builtAt = now;
          lastBuildTime = now;

          // Up to 2 pulses to already-built neighbours within connection radius
          const maxSq = CONNECTION_DISTANCE_PCT * CONNECTION_DISTANCE_PCT;
          const candidates = [];
          for (let i = 0; i < nodes.length; i++) {
            if (i === idx) continue;
            if (nodes[i].builtAt === null) continue;
            if (distSq(nodes[i], nodes[idx]) < maxSq) candidates.push(i);
          }
          // Fisher–Yates shuffle, take first 2
          for (let i = candidates.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
          }
          for (let i = 0; i < Math.min(2, candidates.length); i++) {
            pulses.push({ fromIdx: candidates[i], toIdx: idx, t: 0 });
          }
        } else if (
          buildQueue.length === 0 &&
          nodes.every((n) => n.opacity >= 0.999)
        ) {
          resetAt = now + RESET_DELAY_MS;
        }
      } else if (now >= resetAt) {
        for (const n of nodes) {
          n.builtAt = null;
          n.opacity = 0;
        }
        pulses.length = 0;
        buildQueue = shuffledIndices(nodes.length);
        lastBuildTime = now;
        resetAt = null;
      }

      // ---------- Pulse propagation ----------
      for (let i = pulses.length - 1; i >= 0; i--) {
        pulses[i].t += 1 / PULSE_FRAMES;
        if (pulses[i].t >= 1) pulses.splice(i, 1);
      }

      // ============================== DRAW ==============================
      ctx.clearRect(0, 0, W, H);

      // Background rings (centered, always visible)
      const cx = W / 2;
      const cy = H / 2;
      const ringBase = Math.min(W, H);
      ctx.beginPath();
      ctx.arc(cx, cy, 0.42 * ringBase, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(196,162,55,0.055)';
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, 0.27 * ringBase, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(196,162,55,0.04)';
      ctx.lineWidth = 0.5;
      ctx.stroke();

      // Connections between built nodes
      const maxSq = CONNECTION_DISTANCE_PCT * CONNECTION_DISTANCE_PCT;
      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        if (a.opacity < 0.02) continue;
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          if (b.opacity < 0.02) continue;
          if (distSq(a, b) >= maxSq) continue;
          const minOp = Math.min(a.opacity, b.opacity);
          if (a.group === b.group) {
            const [r, g, bl] = GROUP_COLORS[a.group];
            ctx.strokeStyle = `rgba(${r},${g},${bl},${0.45 * minOp})`;
            ctx.lineWidth = 1;
          } else {
            ctx.strokeStyle = `rgba(160,150,120,${0.18 * minOp})`;
            ctx.lineWidth = 0.5;
          }
          ctx.beginPath();
          ctx.moveTo(a.x * W, a.y * H);
          ctx.lineTo(b.x * W, b.y * H);
          ctx.stroke();
        }
      }

      // Pulses
      for (const p of pulses) {
        const a = nodes[p.fromIdx];
        const b = nodes[p.toIdx];
        const px = (a.x + (b.x - a.x) * p.t) * W;
        const py = (a.y + (b.y - a.y) * p.t) * H;
        const sinT = Math.sin(p.t * Math.PI);
        // Outer glow
        ctx.fillStyle = `rgba(196,162,55,${sinT * 0.2})`;
        ctx.beginPath();
        ctx.arc(px, py, 5, 0, Math.PI * 2);
        ctx.fill();
        // Inner dot
        ctx.fillStyle = `rgba(196,162,55,${sinT * 0.85})`;
        ctx.beginPath();
        ctx.arc(px, py, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Nodes + labels
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.font = '500 10.5px Helvetica, Arial, sans-serif';
      for (const n of nodes) {
        if (n.opacity < 0.02) continue;
        const [r, g, bl] = GROUP_COLORS[n.group];
        const px = n.x * W;
        const py = n.y * H;
        // Outer glow
        ctx.fillStyle = `rgba(${r},${g},${bl},${n.opacity * 0.08})`;
        ctx.beginPath();
        ctx.arc(px, py, n.r + 3, 0, Math.PI * 2);
        ctx.fill();
        // Inner filled circle
        ctx.fillStyle = `rgba(${r},${g},${bl},${n.opacity * 0.75})`;
        ctx.beginPath();
        ctx.arc(px, py, n.r, 0, Math.PI * 2);
        ctx.fill();
        // Label above
        ctx.fillStyle = `rgba(80,75,60,${n.opacity * 0.8})`;
        ctx.fillText(n.label, px, py - n.r - 5);
      }

      rafRef.current = window.requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    rafRef.current = window.requestAnimationFrame(draw);

    return () => {
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section
      ref={containerRef}
      data-testid="app-builder-network-hero"
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor: '#f8f7f4',
        minHeight: '480px',
        height: '85vh',
      }}
    >
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-4"
        style={{ pointerEvents: 'none' }}
        data-testid="app-builder-hero-overlay"
      >
        <span
          data-testid="app-builder-hero-badge"
          style={{
            display: 'inline-block',
            fontSize: '11px',
            letterSpacing: '1.2px',
            textTransform: 'uppercase',
            color: '#b8952e',
            background: '#fdf8ee',
            border: '0.5px solid rgba(212,175,55,0.4)',
            borderRadius: '999px',
            padding: '4px 14px',
            marginBottom: '18px',
          }}
        >
          AI App Builder
        </span>
        <h1
          data-testid="app-builder-hero-headline"
          style={{
            fontSize: '42px',
            fontWeight: 300,
            color: '#1c1b18',
            letterSpacing: '-1.5px',
            lineHeight: 1.1,
            margin: 0,
          }}
        >
          Build without a developer.
        </h1>
        <p
          data-testid="app-builder-hero-subheadline"
          style={{
            fontSize: '15px',
            fontWeight: 400,
            color: '#9a9890',
            margin: '14px 0 0 0',
          }}
        >
          Professional applications, entirely through AI.
        </p>
      </div>
    </section>
  );
};

export default AppBuilderNetwork;
